import { message } from "antd"

import { apiErrorMessage } from "~/api/people-os"
import { useSetDialerSnooze } from "~/hooks/people-os"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

import {
  entryPersonId,
  getDialerCallHistory,
  getDialerCallSummary,
  type IDialerQueueEntry,
  completeDialerSession
} from "~/api/dialer"
import {
  endBurst as endBurstApi,
  getBurst,
  startBurst,
  type BurstLegStatus
} from "~/api/dialer"
import { setCallHold } from "~/api/voice"
import {
  hangUpBurstCall,
  onBurstCallEnded,
  setAutoAnswerNextIncoming,
  useVoiceDevice
} from "~/hooks/use-voice-device"
import useGlobalStore from "~/store/global"
import useVoiceSessionStore, {
  completeCallingHours
} from "~/store/voice-session"
import { ITokenInfo } from "~/types/global"
import type { IPhoneNumber } from "~/types/people-os"
import {
  burstDialNumber,
  orderedDialNumbers,
  type DialNumber
} from "./dial-numbers"
import type { BurstStatus } from "~/containers/people-os/burst-status"
import {
  dropFromTail,
  legStatusFor,
  nextDialIndex,
  nextIndexAfterBurst,
  personVerdictFor,
  rollOnAllowed,
  settledLosers,
  settleRound,
  shouldAdvanceAfterLog,
  splitServiceSkips,
  verdictWindowOpen,
  winnerCallState,
  winnerWatchDelay
} from "~/containers/people-os/burst-round"
import {
  mergeOutcomes,
  type IPersonOutcome
} from "~/containers/people-os/dialer-outcomes"

// The session engine behind the in-row dialer (Final V2, screen 5).
//
// Two parts on purpose:
//
//   DialerRunProvider  state only — queue, position, stage, paused. Mounted in
//                      root.tsx above the route Outlet, so a run survives row
//                      advances, tab switches AND navigating anywhere else in
//                      OrbitShift. Only End Session and Exit Dialer end it.
//   DialerEngine       owns the Twilio device and places the calls. Mounted
//                      ONLY while a run is live.
//
// The split exists because useVoiceDevice builds and registers a Device the
// moment it is called. With it in the provider, every visit to the People page
// fetched a voice token, constructed a Device and enumerated audio hardware
// before the rep had asked to call anybody, and the page took visibly longer
// to load. Mounting the engine only during a run restores what happened when
// the dialer was a modal: no session, no device.
//
// Not a copy of BulkDialer4L's engine either — it drives the same primitives
// (ensureSession, enqueueQueue, reserveCall, markQueueEntryStatus, makeCall,
// logCall) with much smaller state.

export type RunPerson = {
  id: string
  name: string
  title?: string
  phone?: string
  phone_numbers?: IPhoneNumber[]
  tz?: string
  linkedin_url?: string
  location_country?: string
  account?: { id: string; name: string }
  listId?: string
}

type Stage = "idle" | "connecting" | "ringing" | "live" | "wrap"

// Statuses that mean the leg is over. The callee hanging up from their handset
// reaches us this way — Twilio's status webhook, not a browser event — which
// is why the panel used to sit on "ringing" after the other side had gone.
const TERMINAL_CALL_STATUSES = [
  "COMPLETED",
  "NO_ANSWER",
  "BUSY",
  "FAILED",
  "CANCELED",
  "OUTSIDE_HOURS"
]

// Why a call ended. Only an unanswered call rolls on by itself: a conversation
// that happened — or one the rep hung up deliberately — keeps them on the
// screen so they can log it, and "Log & dial next" is what moves on.
type EndReason =
  | "answered"
  | "no_answer"
  | "manual"
  | "skipped"
  | "machine"
  | null

// Beat before an unanswered call rolls to the next number, so the outcome is
// readable and Pause is reachable.

// What the engine hands back so context actions can reach the device without
// the provider ever holding it.
type EngineHandle = {
  hangup: () => void
  endCall: () => Promise<void>
  hangupCall: () => void
  toggleMute: () => void
  sendDigits: (d: string) => void
  setHold: (hold: boolean) => Promise<boolean>
  markSkipped: () => Promise<void>
  log: (v: {
    disposition?: string
    notes?: string
    personId?: string
  }) => Promise<boolean>
}

type DialerRunValue = {
  running: boolean
  paused: boolean
  stage: Stage
  /** How the last call ended. Nothing advances on its own, so the rep reads
   *  this on the wrap screen to see whether it rang, was never dialled, or
   *  they hung up. */
  endReason: EndReason
  /** Twilio's answering-machine verdict for this leg, or null while it is
   *  still being decided. A machine ANSWERS, so the call reads as connected
   *  long before this says whether anybody was there. */
  answeredBy: string | null
  index: number
  queue: RunPerson[]
  current: RunPerson | null
  next: RunPerson | null
  elapsed: number
  completed: number
  muted: boolean
  held: boolean
  deviceStatus: string
  /** AI summary of the call that just ended, once the backend has it. */
  aiSummary: string | null
  /** True when the backend gave up generating one. */
  summaryFailed: boolean
  /** A parallel winner's call is in wrap while its machine verdict can still
   *  arrive. Logging waits for it. */
  verdictPending: boolean
  start: (people: RunPerson[]) => void
  syncQueue: (people: RunPerson[]) => void
  end: () => void
  togglePause: () => void
  toggleMute: () => void
  sendDigits: (d: string) => void
  toggleHold: () => void | Promise<void>
  snooze: () => void
  disconnect: () => void
  /** Call the SAME person again after the call has ended. */
  redial: () => void
  logAndAdvance: (
    v: { disposition?: string; notes?: string; personId?: string },
    advance: boolean
  ) => Promise<void>
  /** A run that advances on its own counts down first, and the rep can stop
   *  it. Null while nothing is pending. */
  /** The callee's line is actually ringing, as Twilio reported it. The stage
   *  turns "ringing" the moment we start dialling, which is earlier. */
  calleeRinging: boolean
  advanceCountdown: number | null
  cancelAdvance: () => void
  advanceNow: () => void
  /** Snoozed while their call is still up: move on once it ends, not now. */
  snoozeCurrentAfterCall: () => void
  dialNumbers: DialNumber[]
  dialNumberIndex: number
  activeDialNumber: DialNumber | null
  /** What each person's line did on this session's most recent attempt at
   *  them. A person with a terminal status here has been worked through. */
  burstStatuses: Record<string, BurstStatus>
  markBurst: (personId: string, status: BurstStatus) => void
  /** Seeded outcomes overlaid with this run's terminal statuses, keyed by
   *  String(person id). */
  outcomes: Map<string, IPersonOutcome>
  seedOutcomes: (m: Map<string, IPersonOutcome>) => void
  /** Ids queued at or after the current index while running. */
  queuedIds: Set<string>
  /** Drops seeded outcomes and this run's statuses. */
  clearOutcomes: () => void
  /** The queue ran out in this run. Reset by start. */
  sessionDone: boolean
  dismissSessionDone: () => void
}

const AUTO_ADVANCE_SECONDS = 5

// Verdicts that mean nobody was there. "unknown" is absent on purpose: it is what a
// quiet human gets, and treating them as a machine ends a real call.
const MACHINE_VERDICTS = [
  "machine_start",
  "machine_end_beep",
  "machine_end_silence",
  "machine_end_other",
  "fax"
]

// What a machine is logged as when the rep is not being asked. Their own word for
// it, so the row reads the same whether they picked it or the dialer did.
const MACHINE_DISPOSITION = "noanswer"

const BURST_POLL_MS = 1200
// Poll budget for settling a won burst's other legs (about two minutes).
const SETTLE_MAX_TICKS = 100
// Time after a parallel winner is seen during which its machine verdict is
// still expected, when no verdict for the winner leg has arrived. The winner's leg is read every BURST_POLL_MS inside it and
// every WINNER_SLOW_POLL_MS after it while the call is live; in wrap the reads
// stop once it has passed.
const VERDICT_WINDOW_MS = 15000
const WINNER_SLOW_POLL_MS = 5000
// How long a burst whose call ended from the far side is read for a machine
// verdict before it is treated as an answered call that ended, and the gap
// between reads.
const FAR_END_WAIT_MS = 2500
const FAR_END_CHECK_MS = 600

// Gap between a round nobody answered and the next round opening.
export const BURST_BREATHER_MS = 4000

// Resolves with the run's session id once the engine has one, or null when
// it could not open one.
type SessionWait = {
  promise: Promise<number | null>
  resolve: (id: number | null) => void
}

const waitForSession = (): SessionWait => {
  let resolve: (id: number | null) => void = () => undefined
  const promise = new Promise<number | null>((r) => {
    resolve = r
  })
  return { promise, resolve }
}

// Twilio's own words for a finished leg, said in the five the grid colours by.
const LEG_STATUS: Record<BurstLegStatus, BurstStatus> = {
  pending: "ringing",
  dialing: "ringing",
  won: "connected",
  lost: "cancelled",
  abandoned: "cancelled",
  machine: "cancelled",
  skipped: "cancelled",
  snoozed: "cancelled",
  failed: "rejected",
  no_answer: "noanswer",
  busy: "rejected",
  canceled: "cancelled"
}

const LEG_SETTLED: BurstLegStatus[] = [
  "won",
  "lost",
  "abandoned",
  "machine",
  "skipped",
  "snoozed",
  "failed",
  "no_answer",
  "busy",
  "canceled"
]

const DialerRunContext = createContext<DialerRunValue | null>(null)

export function useDialerRun() {
  const ctx = useContext(DialerRunContext)
  if (!ctx) {
    throw new Error("useDialerRun must be used inside DialerRunProvider")
  }
  return ctx
}

export default function DialerRunProvider({
  children
}: {
  children: React.ReactNode
}) {
  const engineRef = useRef<EngineHandle | null>(null)
  const liveSessionRef = useRef<number | null>(null)
  const outerToken = useGlobalStore((store) => store.tokenInfo)

  const [running, setRunning] = useState(false)
  const [paused, setPaused] = useState(false)
  const [stage, setStage] = useState<Stage>("idle")
  const [dialNumberIndex, setDialNumberIndex] = useState(0)
  const [queue, setQueue] = useState<RunPerson[]>([])
  const [index, setIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [logged, setLogged] = useState<Set<string>>(new Set())
  const [held, setHeld] = useState(false)
  const { mutateAsync: setSnooze } = useSetDialerSnooze()
  const [muted, setMuted] = useState(false)
  const [deviceStatus, setDeviceStatus] = useState("idle")
  const [aiSummary, setAiSummary] = useState<string | null>(null)
  const currentIdRef = useRef<string | null>(null)
  const [endReason, setEndReason] = useState<EndReason>(null)
  const [burstStatuses, setBurstStatuses] = useState<
    Record<string, BurstStatus>
  >({})
  const [burstId, setBurstId] = useState<number | null>(null)
  const [burstWinnerId, setBurstWinnerId] = useState<string | null>(null)
  const [burstSize, setBurstSize] = useState(0)
  const burstOpeningRef = useRef(false)
  // A burst that cannot be opened must not be opened again on the next render.
  // Nothing about the attempt changes between tries, so retrying is a loop that
  // stacks one message per pass and leaves the rows it marked reading Ringing.
  const burstFailedRef = useRef(false)
  const [answeredBy, setAnsweredBy] = useState<string | null>(null)
  const [summaryFailed, setSummaryFailed] = useState(false)
  const sessionWaitRef = useRef<SessionWait | null>(null)
  // Incremented by start and end; async work compares it to tell whether the
  // run it began in is still the live one.
  const runTokenRef = useRef(0)
  // The contacts the open or last burst rang, and the queue index it began at.
  const burstContactIdsRef = useRef<string[]>([])
  const burstStartRef = useRef(0)
  // The contacts sent for the open burst that the service kept, in queue
  // order, and whether the governor capped its lines.
  const burstSentIdsRef = useRef<string[]>([])
  const burstCappedRef = useRef(false)
  // People dropped from this run; syncQueue keeps them out of the tail.
  const skippedIdsRef = useRef<Set<string>>(new Set())
  const [nextBurstAt, setNextBurstAt] = useState<number | null>(null)
  const [seededOutcomes, setSeededOutcomes] = useState<
    Map<string, IPersonOutcome>
  >(() => new Map())
  const [sessionDone, setSessionDone] = useState(false)
  // A burst that has a winner, polled until its other legs settle.
  const [settling, setSettling] = useState<{
    burstId: number
    winnerId: string
  } | null>(null)

  // Latest stage, burst and winner, for the burst call's disconnect listener.
  const stageRef = useRef<Stage>("idle")
  const burstIdRef = useRef<number | null>(null)
  const burstWinnerIdRef = useRef<string | null>(null)
  useEffect(() => {
    stageRef.current = stage
    burstIdRef.current = burstId
    burstWinnerIdRef.current = burstWinnerId
  }, [stage, burstId, burstWinnerId])
  // The burst the rep ended with Disconnect, the burst a far-side end is being
  // settled for, and the burst whose machine winner was rolled past.
  const repEndedBurstRef = useRef<number | null>(null)
  const farEndBurstRef = useRef<number | null>(null)
  const rolledBurstsRef = useRef<Set<number>>(new Set())
  // The burst whose winner the rep is logging, and when the open burst's
  // winner was seen.
  const loggingBurstRef = useRef<number | null>(null)
  const wonAtRef = useRef(0)
  const [verdictPending, setVerdictPending] = useState(false)

  const parallelLines = useVoiceSessionStore((s) => s.parallelLines)
  const burstFromNumber = useVoiceSessionStore((s) => s.callerNumber)
  const burstMode = parallelLines > 1

  // The contacts this burst is ringing. Taken from the queue at the point the rep
  // has reached, so a burst is the next N people and nothing else.
  const burstContacts = useMemo(
    () => (burstMode ? queue.slice(index, index + parallelLines) : []),
    [burstMode, queue, index, parallelLines]
  )

  const current = burstMode
    ? burstWinnerId
      ? (queue.find((p) => String(p.id) === burstWinnerId) ?? null)
      : null
    : (queue[index] ?? null)
  useEffect(() => {
    currentIdRef.current = current ? String(current.id) : null
  }, [current])
  // With a burst winner on the line, the first entry after everyone that
  // round rang; otherwise the entry after the index.
  const next =
    queue[
      nextDialIndex(
        queue.map((p) => String(p.id)),
        index,
        burstMode && burstWinnerId
          ? {
              rungIds: burstContactIdsRef.current,
              fallback: burstStartRef.current + Math.max(burstSize, 1)
            }
          : null
      )
    ] ?? null
  const dialNumbers = useMemo(
    () => (current ? orderedDialNumbers(current) : []),
    [current]
  )
  const activeDialNumber = dialNumbers[dialNumberIndex] ?? null

  // The live timer. Only runs while a call is up, so a wrap-up screen never
  // keeps counting.
  useEffect(() => {
    if (stage !== "live") return
    setElapsed(0)
    const t = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [stage])

  const start = useCallback((people: RunPerson[]) => {
    if (!people.length) {
      message.error("Nobody to call")
      return
    }
    runTokenRef.current += 1
    liveSessionRef.current = null
    sessionWaitRef.current?.resolve(null)
    sessionWaitRef.current = waitForSession()
    skippedIdsRef.current = new Set()
    burstContactIdsRef.current = []
    setNextBurstAt(null)
    setSessionDone(false)
    setQueue(people)
    setIndex(0)
    setLogged(new Set())
    setBurstStatuses({})
    setSettling(null)
    burstFailedRef.current = false
    setElapsed(0)
    setPaused(false)
    setHeld(false)
    setStage("connecting")
    setRunning(true)
    setAiSummary(null)
    setEndReason(null)
    setSummaryFailed(false)
  }, [])

  // Keeps a running queue in step with who is callable now.
  //
  // The queue used to be a snapshot taken at Start Session, so anyone who
  // became callable while the rep was dialling - a calling window widened, a
  // snooze lifted, a number enriched - sat outside the run, and the progress
  // total disagreed with the Callable tab beside it.
  //
  // Everyone up to and including the person on the phone is left alone: they
  // have been dialled, or are being dialled, and a list that reshuffles under
  // a live call is worse than one that is a little behind. Only the tail is
  // replaced.
  const syncQueue = useCallback(
    (people: RunPerson[]) => {
      setQueue((prev) => {
        if (!prev.length) return prev
        // A burst holds its whole round in place, not just the entry at index.
        const keep = burstSize
          ? Math.max(index + 1, burstStartRef.current + burstSize)
          : index + 1
        const dialled = prev.slice(0, keep)
        const seen = new Set(dialled.map((p) => String(p.id)))
        const next = [
          ...dialled,
          ...people.filter(
            (p) =>
              !seen.has(String(p.id)) &&
              !skippedIdsRef.current.has(String(p.id))
          )
        ]
        // Same people in the same order means no state change, so the effect
        // driving this cannot loop.
        if (
          next.length === prev.length &&
          next.every((p, i) => String(p.id) === String(prev[i].id))
        ) {
          return prev
        }
        return next
      })
    },
    [index, burstSize]
  )

  // The ONLY way a session ends: End Session and Exit Dialer both land here.
  //
  // Started BEFORE the state reset, because setRunning(false) unmounts the
  // engine and nulls the handle. The promise it returns keeps running after
  // the engine has gone — it closes over what it needs — so the server calls
  // still land even though nothing is left on screen to wait for them.
  const closeBurst = useCallback(() => {
    const id = burstId
    setBurstId(null)
    setBurstWinnerId(null)
    setBurstSize(0)
    setVerdictPending(false)
    setAutoAnswerNextIncoming(false)
    hangUpBurstCall()
    if (id && outerToken) {
      void endBurstApi(outerToken, id).catch(() => undefined)
    }
  }, [burstId, outerToken])

  const end = useCallback(() => {
    closeBurst()
    if (engineRef.current) {
      void engineRef.current.endCall()
    } else if (outerToken && liveSessionRef.current) {
      void completeDialerSession(outerToken, liveSessionRef.current).catch(
        () => undefined
      )
    }
    liveSessionRef.current = null
    runTokenRef.current += 1
    sessionWaitRef.current?.resolve(null)
    setNextBurstAt(null)
    setRunning(false)
    setPaused(false)
    setStage("idle")
    setQueue([])
    setIndex(0)
    setElapsed(0)
    setHeld(false)
  }, [outerToken, closeBurst])

  // The queue has run out: closes the session and stops the run.
  const finishQueue = useCallback(() => {
    void engineRef.current?.endCall()
    setNextBurstAt(null)
    setRunning(false)
    setStage("idle")
    setSessionDone(true)
  }, [])

  const snoozedDuringCallRef = useRef(false)
  const autoAdvance = useVoiceSessionStore((s) => s.autoAdvance)
  const [calleeRinging, setCalleeRinging] = useState(false)
  const [advanceCountdown, setAdvanceCountdown] = useState<number | null>(null)

  const advance = useCallback(() => {
    setAiSummary(null)
    setSummaryFailed(false)
    setEndReason(null)
    setElapsed(0)
    setHeld(false)
    // Decided outside the state updater: React can invoke an updater more than
    // once, so anything with a side effect cannot live inside one.
    if (index + 1 >= queue.length) {
      finishQueue()
      message.success("Session complete")
      return
    }
    engineRef.current?.hangupCall()
    setIndex(index + 1)
    setDialNumberIndex(0)
    setStage("connecting")
  }, [queue.length, index, finishQueue])

  // Ends the round the winner came from and moves past everyone it rang.
  const advancePastBurst = useCallback(() => {
    setAiSummary(null)
    setSummaryFailed(false)
    setEndReason(null)
    setElapsed(0)
    setHeld(false)
    const at = nextIndexAfterBurst(
      queue.map((p) => String(p.id)),
      burstContactIdsRef.current,
      burstStartRef.current + Math.max(burstSize, 1)
    )
    closeBurst()
    if (at >= queue.length) {
      finishQueue()
      message.success("Session complete")
      return
    }
    setIndex(at)
    setDialNumberIndex(0)
    setStage("connecting")
  }, [queue, burstSize, closeBurst, finishQueue])

  // Marks a voicemail winner as no answer, hangs up the rep's leg, steps past
  // the round's contacts and starts the next round after BURST_BREATHER_MS.
  // Runs once per burst, and not while the rep is logging that burst.
  const rollPastMachineWinner = useCallback(
    (winnerId: string, watchedBurst: number) => {
      if (
        !rollOnAllowed(
          watchedBurst,
          rolledBurstsRef.current,
          loggingBurstRef.current
        )
      ) {
        return
      }
      if (burstIdRef.current !== watchedBurst) return
      rolledBurstsRef.current.add(watchedBurst)
      setVerdictPending(false)
      setBurstStatuses((prev) =>
        prev[winnerId] === "noanswer"
          ? prev
          : { ...prev, [winnerId]: "noanswer" }
      )
      setSettling(null)
      setAiSummary(null)
      setSummaryFailed(false)
      setEndReason(null)
      setAnsweredBy(null)
      setElapsed(0)
      setHeld(false)
      const at = nextIndexAfterBurst(
        queue.map((p) => String(p.id)),
        burstContactIdsRef.current,
        burstStartRef.current + Math.max(burstSize, 1)
      )
      closeBurst()
      message.info({
        key: "burst-machine",
        content: "Voicemail picked up. Logged as no answer"
      })
      if (at >= queue.length) {
        finishQueue()
        message.success("Session complete")
        return
      }
      setIndex(at)
      setDialNumberIndex(0)
      setNextBurstAt(Date.now() + BURST_BREATHER_MS)
      setStage("connecting")
    },
    [queue, burstSize, closeBurst, finishQueue]
  )
  const rollRef = useRef(rollPastMachineWinner)
  useEffect(() => {
    rollRef.current = rollPastMachineWinner
  }, [rollPastMachineWinner])

  // Settles a won burst whose call ended without the rep pressing Disconnect.
  // Reads the burst until FAR_END_WAIT_MS has passed, at least once: a machine
  // winner rolls on, otherwise the run moves to wrap as an answered call.
  // Stops once the stage is no longer live, the burst changed, or the rep
  // ended it.
  const concludeFarEnd = useCallback(
    async (watchedBurst: number, winnerId: string) => {
      if (!outerToken || farEndBurstRef.current === watchedBurst) return
      farEndBurstRef.current = watchedBurst
      const stale = () =>
        burstIdRef.current !== watchedBurst ||
        stageRef.current !== "live" ||
        repEndedBurstRef.current === watchedBurst
      const deadline = Date.now() + FAR_END_WAIT_MS
      for (;;) {
        if (stale()) return
        try {
          const { data } = await getBurst(outerToken, watchedBurst)
          if (stale()) return
          if (legStatusFor(data.legs, winnerId) === "machine") {
            rollRef.current(winnerId, watchedBurst)
            return
          }
        } catch {
          // The next read retries.
        }
        if (Date.now() >= deadline) break
        await new Promise((r) => setTimeout(r, FAR_END_CHECK_MS))
      }
      if (stale()) return
      setEndReason("answered")
      setStage("wrap")
    },
    [outerToken]
  )

  // The auto-answered burst call disconnecting while its winner is live.
  useEffect(
    () =>
      onBurstCallEnded(() => {
        const id = burstIdRef.current
        const winnerId = burstWinnerIdRef.current
        if (id == null || !winnerId || stageRef.current !== "live") return
        void concludeFarEnd(id, winnerId)
      }),
    [concludeFarEnd]
  )

  const logAndAdvance = useCallback(
    async (
      v: { disposition?: string; notes?: string; personId?: string },
      shouldAdvance: boolean
    ) => {
      const loggedBurst = burstMode && burstWinnerId ? burstIdRef.current : null
      if (loggedBurst != null) loggingBurstRef.current = loggedBurst
      const ok = await (engineRef.current?.log(v) ?? Promise.resolve(false))
      if (!ok) {
        if (loggingBurstRef.current === loggedBurst) {
          loggingBurstRef.current = null
        }
        return
      }
      // Counts PEOPLE logged, not log events. It incremented per call to this
      // function, so logging the same contact twice - a redial, or just
      // pressing Log call again - read as "3 / 1" on the session bar against a
      // one-person queue.
      const personId = v.personId ?? (current ? String(current.id) : "")
      setLogged((prev) => {
        if (!personId || prev.has(personId)) return prev
        const nextSet = new Set(prev)
        nextSet.add(personId)
        return nextSet
      })
      if (burstMode && burstWinnerId && personId === burstWinnerId) {
        if (
          shouldAdvanceAfterLog(
            loggedBurst,
            burstIdRef.current,
            rolledBurstsRef.current
          )
        ) {
          advancePastBurst()
        }
        return
      }
      if (shouldAdvance) advance()
      else if (!next) {
        // The last contact has been logged and there is nothing to dial next.
        // Say the list is finished rather than leaving the rep looking at a
        // panel that has quietly stopped being useful.
        finishQueue()
        message.success("List finished — that was the last contact.")
      }
    },
    [
      advance,
      advancePastBurst,
      burstMode,
      burstWinnerId,
      current,
      next,
      finishQueue
    ]
  )

  // Snooze SKIPS the contact and REMEMBERS it: hang up, mark the queue entry
  // SKIPPED, record the snooze, move on.
  //
  // It first moved them to the back of the queue, which meant a snoozed person
  // came round again inside the same session. Then it skipped without
  // recording, so the next session offered them again and nothing anywhere
  // knew the rep had decided. It persists now, with no expiry - the rep is
  // saying "stop putting this person in front of me", and only they undo it.
  const snooze = useCallback(() => {
    if (!current) return
    if (burstMode && burstWinnerId) hangUpBurstCall()
    engineRef.current?.hangup()
    void engineRef.current?.markSkipped()
    // Fire and forget: the queue must move on at once, and the mutation
    // updates the snooze list optimistically so the row leaves the callable
    // side without waiting for the round trip.
    void setSnooze({ championId: String(current.id), snoozed: true }).catch(
      () => {
        // A failed snooze is worth telling the rep about - they will otherwise
        // see this person again and assume the button does nothing.
        message.warning(
          "Skipped, but could not save the snooze. They may come back in a later session."
        )
      }
    )
    setHeld(false)
    setElapsed(0)
    if (burstMode && burstWinnerId) advancePastBurst()
    else advance()
  }, [current, advance, advancePastBurst, burstMode, burstWinnerId, setSnooze])

  // NOTHING rolls on by itself. The queue advances only when the rep says so.
  //
  // An unanswered call used to wait 2.5s and dial the next person. That beat
  // was measured against Twilio's 30 second ring, where it reads as a pause -
  // but the two cases that never ring cost only the gap itself: a person with
  // no number, skipped without dialling, and a number Twilio rejects outright.
  // A run of those advanced every 2.5 seconds and the queue visibly ran away
  // from the rep.
  //
  // Rather than tune the number, the auto-advance is gone. Every call now ends
  // on the wrap screen with the outcome in front of the rep, and "Log & dial
  // next" is the only thing that moves the queue - the same way a connected
  // call and a rep-ended call already behaved.

  // Just flips the flag. Nothing advances on its own any more, so coming off
  // pause leaves the rep exactly where they were.
  const togglePause = useCallback(() => setPaused((p) => !p), [])

  // Said once per press, outside the setter.
  // ENG-4380. Said "the current call continues" whether or not one was up, so
  // pausing with nothing dialling announced a call that did not exist. What
  // pause actually guarantees is that the NEXT person is not dialled, which is
  // true in both cases.
  useEffect(() => {
    if (running && paused) {
      message.info("Dialing paused. Next person will not be dialled")
    }
  }, [paused, running])

  // Auto-advance waits, visibly, instead of moving the moment a call ends.
  //
  // Advancing immediately reassigned the call the rep was still dispositioning,
  // so their outcome landed on the next person: in one session eleven of
  // twenty-seven calls disagreed with what Twilio recorded. The wait gives the
  // rep the call they just had for long enough to log it, and a way to stop the
  // run without ending the session.
  useEffect(() => {
    if (!running || paused || stage !== "wrap") {
      setAdvanceCountdown(null)
      return
    }
    // A machine still needs no conversation recorded, so it keeps its countdown
    // and its disposition is filled in for the rep either way. What it does not
    // do any more is override the rep: with auto advance off they press Log and
    // dial next themselves, same as every other ending.
    const machine = endReason === "machine"
    if (!autoAdvance) {
      setAdvanceCountdown(null)
      return
    }
    // Only a ring nobody picked up. Every other ending leaves the rep with
    // something to record: a call that connected has an outcome whoever hung
    // up first, and one they disconnected or skipped was already their
    // decision. Moving on for them would take the disposition off them, which
    // is the thing that went wrong when this advanced on its own.
    if (endReason !== "no_answer" && !machine) {
      setAdvanceCountdown(null)
      return
    }
    // A ring nobody answered is worth trying their other number for; a greeting
    // is not. The number works and they are not at it, so the queue moves on
    // rather than leaving the rep parked on a wrap screen with no countdown and
    // nothing to press - the retry effect only fires for no_answer.
    if (!machine && dialNumberIndex + 1 < dialNumbers.length) {
      setAdvanceCountdown(null)
      return
    }
    setAdvanceCountdown(AUTO_ADVANCE_SECONDS)
  }, [
    running,
    paused,
    stage,
    autoAdvance,
    endReason,
    current?.id,
    dialNumberIndex,
    dialNumbers.length
  ])

  // A person snoozed while their call was connected. The row has already left
  // the callable side, but the conversation has not, so the run waits for it to
  // finish and then moves on by itself - the rep does not have to come back to
  // a call they have already dealt with.
  useEffect(() => {
    if (!running || paused || stage !== "wrap") return
    if (!snoozedDuringCallRef.current) return
    snoozedDuringCallRef.current = false
    advance()
  }, [running, paused, stage, advance])

  useEffect(() => {
    setDialNumberIndex(0)
  }, [current?.id])

  useEffect(() => {
    if (!running || paused || stage !== "wrap") return
    if (endReason !== "no_answer") return
    if (dialNumberIndex + 1 >= dialNumbers.length) return
    const nextNumber = dialNumbers[dialNumberIndex + 1]
    setDialNumberIndex(dialNumberIndex + 1)
    setEndReason(null)
    setStage("connecting")
    message.info(
      `No answer. Trying their ${nextNumber.label.toLowerCase()} number`
    )
  }, [running, paused, stage, endReason, dialNumberIndex, dialNumbers])

  useEffect(() => {
    if (stage === "connecting") snoozedDuringCallRef.current = false
  }, [stage, current?.id])

  // The breather between rounds: clears nextBurstAt when it is due. Pausing or
  // ending the run clears it straight away.
  useEffect(() => {
    if (nextBurstAt == null) return
    if (!running || paused) {
      setNextBurstAt(null)
      return
    }
    const t = setTimeout(
      () => setNextBurstAt(null),
      Math.max(0, nextBurstAt - Date.now())
    )
    return () => clearTimeout(t)
  }, [nextBurstAt, running, paused])

  // Opens a burst: the server places the rep's own leg into an empty conference and
  // only then rings the contacts, so the first person to answer never arrives to an
  // empty room. The browser answers its own leg in silence.
  useEffect(() => {
    if (!burstMode || !running || paused || nextBurstAt != null) return
    if (burstId || burstOpeningRef.current || burstFailedRef.current) return
    if (burstWinnerId || stage === "live" || stage === "wrap") return
    if (!outerToken || !burstContacts.length) return

    burstOpeningRef.current = true
    const runToken = runTokenRef.current
    const contacts = burstContacts
    burstContactIdsRef.current = contacts.map((c) => String(c.id))
    burstSentIdsRef.current = burstContactIdsRef.current
    burstCappedRef.current = false
    burstStartRef.current = index
    setBurstSize(contacts.length)
    setBurstStatuses((prev) => {
      const next = { ...prev }
      for (const c of contacts) next[String(c.id)] = "ringing"
      return next
    })
    setStage("ringing")
    setAutoAnswerNextIncoming(true)

    const fail = (content: string, key = "burst-start-failed") => {
      burstFailedRef.current = true
      setAutoAnswerNextIncoming(false)
      setStage("idle")
      setRunning(false)
      setBurstStatuses((prev) => {
        const next = { ...prev }
        for (const c of contacts) delete next[String(c.id)]
        return next
      })
      message.error({ content, key })
    }

    ;(async () => {
      try {
        const sessionId =
          liveSessionRef.current ??
          (await (sessionWaitRef.current?.promise ?? Promise.resolve(null)))
        if (runToken !== runTokenRef.current) return
        if (sessionId == null) {
          fail(
            "Could not open a calling session. The session has stopped.",
            "dialer-session-failed"
          )
          return
        }
        const res = await startBurst(outerToken, {
          contacts: contacts.map((c) => ({
            champion_id: String(c.id),
            phone_number: burstDialNumber(c)
          })),
          session_id: sessionId,
          from_number: burstFromNumber ?? null,
          lines: contacts.length
        })
        const openedId = res.data.burst_id
        if (runToken !== runTokenRef.current) {
          if (openedId != null) {
            void endBurstApi(outerToken, openedId).catch(() => undefined)
          }
          return
        }
        const { sent, skipped } = splitServiceSkips(
          burstSentIdsRef.current,
          res.data
        )
        const gone = new Set(skipped)
        burstSentIdsRef.current = sent
        burstCappedRef.current = Boolean(res.data.governor?.capped)

        if (gone.size) {
          for (const id of gone) skippedIdsRef.current.add(id)
          setQueue((prev) => dropFromTail(prev, gone, burstStartRef.current))
          setBurstStatuses((prev) => {
            const next = { ...prev }
            for (const id of gone) delete next[id]
            return next
          })
        }
        const outsideCount = (res.data.outside_hours ?? []).length
        if (outsideCount) {
          message.info(`${outsideCount} skipped — outside their calling hours`)
        }
        const duplicateCount = (res.data.duplicate_ids ?? []).length
        if (duplicateCount) {
          message.info({
            key: "burst-duplicates",
            content: `${duplicateCount} skipped — number already dialled this session`
          })
        }
        const invalidCount = (res.data.invalid_numbers ?? []).length
        if (invalidCount) {
          message.info({
            key: "burst-invalid-numbers",
            content: `${invalidCount} skipped — number needs a country code`
          })
        }

        // No burst opened: the round is dropped from the queue and the next
        // round opens from the same index.
        if (openedId == null) {
          burstContactIdsRef.current = []
          setBurstSize(0)
          setAutoAnswerNextIncoming(false)
          setStage("connecting")
          return
        }
        setBurstId(openedId)

        const { governor, lines_queued } = res.data
        if (governor?.capped) {
          message.warning({
            key: "burst-governor",
            content:
              governor.reason ??
              `Running ${lines_queued} lines — abandon rate ${governor.abandoned_rate_pct}%, limit ${governor.ceiling_pct}%`
          })
        }
      } catch (e) {
        if (runToken !== runTokenRef.current) return
        // Say what the server said. Swallowing it printed the same sentence
        // whatever had gone wrong - a missing column, a paused session, a
        // number with no caller id - and the rep, and whoever they asked,
        // had nothing to go on.
        fail(
          apiErrorMessage(
            e,
            "Could not start the parallel dial. The session has stopped."
          )
        )
      } finally {
        burstOpeningRef.current = false
      }
    })()
  }, [
    burstMode,
    running,
    paused,
    nextBurstAt,
    burstId,
    burstWinnerId,
    stage,
    outerToken,
    burstContacts,
    burstFromNumber,
    index
  ])

  // Finishes the run when a round with no burst left the index past the end of
  // the queue.
  useEffect(() => {
    if (!burstMode || !running || burstId || burstWinnerId) return
    if (stage !== "connecting" || index < queue.length) return
    finishQueue()
    message.success("Session complete")
  }, [
    burstMode,
    running,
    burstId,
    burstWinnerId,
    stage,
    index,
    queue.length,
    finishQueue
  ])

  // Follows the lines. Nothing else tells the screen that one of them answered: the
  // provider settles each leg against the service out of band.
  useEffect(() => {
    if (!burstId || !outerToken || burstWinnerId) return
    let stopped = false

    const tick = async () => {
      try {
        const { data } = await getBurst(outerToken, burstId)
        if (stopped) return

        const legIds = data.legs
          .map((l) => l.champion_id ?? l.people_id)
          .filter((id) => id != null)
          .map(String)
        const { rung, undialled } = settleRound(
          burstSentIdsRef.current,
          legIds,
          burstCappedRef.current
        )
        burstContactIdsRef.current = rung

        setBurstStatuses((prev) => {
          const next = { ...prev }
          for (const id of undialled) {
            if (next[id] === "ringing") delete next[id]
          }
          for (const leg of data.legs) {
            const personId = leg.champion_id ?? leg.people_id
            if (personId == null) continue
            next[String(personId)] = LEG_STATUS[leg.status] ?? "ringing"
          }
          return next
        })

        const winner = data.legs.find((l) => l.status === "won")
        if (winner) {
          const personId = winner.champion_id ?? winner.people_id
          if (personId != null) {
            // The queue index moves to whoever answered, so logging and the wrap
            // screen act on the person the rep is actually talking to.
            const at = queue.findIndex((p) => String(p.id) === String(personId))
            if (at >= 0) setIndex(at)
            wonAtRef.current = Date.now()
            setVerdictPending(true)
            setBurstWinnerId(String(personId))
            setSettling({ burstId, winnerId: String(personId) })
            setStage("live")
          }
          return
        }

        const everySettled =
          data.legs.length > 0 &&
          data.legs.every((l) => LEG_SETTLED.includes(l.status))
        if (data.ended_at || everySettled) {
          // No winner: steps past everyone this round rang, then waits
          // BURST_BREATHER_MS before the open effect starts the next round.
          stopped = true
          setBurstId(null)
          const at = nextIndexAfterBurst(
            queue.map((p) => String(p.id)),
            burstContactIdsRef.current,
            burstStartRef.current + Math.max(burstSize, 1)
          )
          if (at >= queue.length) {
            closeBurst()
            finishQueue()
            message.success("Session complete")
            return
          }
          setIndex(at)
          setNextBurstAt(Date.now() + BURST_BREATHER_MS)
          setStage("connecting")
        }
      } catch {
        // A poll that fails is a poll; the next one answers. Tearing the burst down
        // over one bad response would hang up lines that are still ringing.
      }
    }

    void tick()
    const timer = setInterval(tick, BURST_POLL_MS)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [
    burstId,
    outerToken,
    burstWinnerId,
    queue,
    burstSize,
    closeBurst,
    finishQueue
  ])

  // Polls a won burst until every other leg has settled or the burst ended,
  // writing each settled loser's status.
  useEffect(() => {
    if (!settling || !outerToken || !running) return
    let stopped = false
    let ticks = 0

    const tick = async () => {
      ticks += 1
      try {
        const { data } = await getBurst(outerToken, settling.burstId)
        if (stopped) return
        const { settled, allSettled } = settledLosers(
          data.legs,
          settling.winnerId,
          LEG_SETTLED
        )
        if (settled.length) {
          setBurstStatuses((prev) => {
            let changed = false
            const next = { ...prev }
            for (const { id, status } of settled) {
              const mapped = LEG_STATUS[status]
              if (next[id] !== mapped) {
                next[id] = mapped
                changed = true
              }
            }
            return changed ? next : prev
          })
        }
        if (allSettled || data.ended_at || ticks >= SETTLE_MAX_TICKS) {
          stopped = true
          setSettling(null)
        }
      } catch {
        if (ticks >= SETTLE_MAX_TICKS) {
          stopped = true
          setSettling(null)
        }
      }
    }

    void tick()
    const timer = setInterval(() => {
      if (!stopped) void tick()
    }, BURST_POLL_MS)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [settling, outerToken, running])

  // Polls a won burst while the rep is live or wrapping up, and rolls on when
  // the winner's leg turns out to be a machine. While live, a burst or winner
  // leg that has ended hands over to concludeFarEnd. Each read is sent after
  // the previous one returned, BURST_POLL_MS apart inside VERDICT_WINDOW_MS of
  // the win and WINNER_SLOW_POLL_MS after it. A person's verdict on the winner
  // leg, or the first read after the window, clears verdictPending; in wrap the
  // poll then stops. A burst the rep is logging stops the poll.
  useEffect(() => {
    if (!burstMode || !running || !burstId || !burstWinnerId || !outerToken)
      return
    if (stage !== "live" && stage !== "wrap") return
    const watchedBurst = burstId
    const winnerId = burstWinnerId
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const stop = () => {
      stopped = true
      clearTimeout(timer)
    }

    const tick = async () => {
      try {
        const { data } = await getBurst(outerToken, watchedBurst)
        if (stopped) return
        if (loggingBurstRef.current === watchedBurst) {
          stop()
          return
        }
        const state = winnerCallState(data.legs, winnerId, data.ended_at)
        if (state === "machine") {
          stop()
          rollRef.current(winnerId, watchedBurst)
          return
        }
        if (state === "ended" && stage === "live") {
          stop()
          void concludeFarEnd(watchedBurst, winnerId)
          return
        }
        if (personVerdictFor(data.legs, winnerId, MACHINE_VERDICTS)) {
          setVerdictPending(false)
          if (stage === "wrap") {
            stop()
            return
          }
        }
      } catch {
        // The next tick retries.
      }
      if (stopped) return
      if (loggingBurstRef.current === watchedBurst) {
        stop()
        return
      }
      const now = Date.now()
      if (!verdictWindowOpen(wonAtRef.current, now, VERDICT_WINDOW_MS)) {
        setVerdictPending(false)
        if (stage === "wrap") return
      }
      timer = setTimeout(
        () => void tick(),
        winnerWatchDelay(
          wonAtRef.current,
          now,
          VERDICT_WINDOW_MS,
          BURST_POLL_MS,
          WINNER_SLOW_POLL_MS
        )
      )
    }

    void tick()
    return stop
  }, [
    burstMode,
    running,
    burstId,
    burstWinnerId,
    outerToken,
    stage,
    concludeFarEnd
  ])

  const markBurst = useCallback((personId: string, status: BurstStatus) => {
    setBurstStatuses((prev) =>
      prev[personId] === status ? prev : { ...prev, [personId]: status }
    )
  }, [])

  useEffect(() => {
    const id = current ? String(current.id) : null
    if (!id || !running) return
    if (stage === "ringing" || stage === "connecting") {
      markBurst(id, "ringing")
      return
    }
    if (stage === "live") {
      markBurst(id, "connected")
      return
    }
    if (stage !== "wrap") return
    if (endReason === "answered") {
      markBurst(id, "connected")
    } else if (endReason === "no_answer") {
      markBurst(id, "noanswer")
    } else if (endReason === "skipped") {
      markBurst(id, "cancelled")
    } else if (endReason === "manual") {
      setBurstStatuses((prev) =>
        prev[id] === "connected" ? prev : { ...prev, [id]: "noanswer" }
      )
    }
  }, [stage, endReason, current, running, markBurst])

  const outcomes = useMemo(
    () => mergeOutcomes(seededOutcomes, burstStatuses),
    [seededOutcomes, burstStatuses]
  )

  const queuedIds = useMemo(
    () => new Set(running ? queue.slice(index).map((p) => String(p.id)) : []),
    [running, queue, index]
  )

  const value: DialerRunValue = {
    dialNumbers,
    dialNumberIndex,
    activeDialNumber,
    calleeRinging,
    advanceCountdown,
    cancelAdvance: () => setAdvanceCountdown(null),
    advanceNow: () => {
      setAdvanceCountdown(null)
      if (endReason === "machine" && current) {
        void logAndAdvance(
          { disposition: MACHINE_DISPOSITION, personId: String(current.id) },
          false
        )
      }
      if (burstMode && burstWinnerId) advancePastBurst()
      else advance()
    },
    snoozeCurrentAfterCall: () => {
      snoozedDuringCallRef.current = true
    },
    syncQueue,
    running,
    paused,
    stage,
    endReason,
    answeredBy,
    index,
    queue,
    current,
    next,
    elapsed,
    completed: logged.size,
    muted,
    held,
    deviceStatus,
    aiSummary,
    summaryFailed,
    verdictPending,
    start,
    end,
    togglePause,
    toggleMute: () => engineRef.current?.toggleMute(),
    sendDigits: (d) => engineRef.current?.sendDigits(d),
    toggleHold: async () => {
      const want = !held
      const ok = await (engineRef.current?.setHold(want) ??
        Promise.resolve(false))
      if (ok) setHeld(want)
    },
    // Dials the current person again rather than moving on. The engine places
    // a call whenever stage becomes "connecting", so re-entering that stage
    // without touching the index is the whole of it; the per-call state is
    // cleared first so the next leg does not inherit the last one's summary,
    // mute or hold.
    redial: () => {
      // Pausing means the queue stops after this call. Re-arming the current
      // person sets stage back to "connecting", which the dial effect ignores
      // while paused - and then fires the moment the rep resumes, redialling
      // someone they had already finished with instead of moving on.
      if (paused) return
      setAiSummary(null)
      setSummaryFailed(false)
      setEndReason(null)
      setAnsweredBy(null)
      setElapsed(0)
      setHeld(false)
      setMuted(false)
      setStage("connecting")
    },
    snooze,
    disconnect: () => {
      setEndReason("manual")
      if (burstMode && burstWinnerId) {
        repEndedBurstRef.current = burstId
        hangUpBurstCall()
      }
      engineRef.current?.hangup()
      setStage("wrap")
    },
    logAndAdvance,
    burstStatuses,
    markBurst,
    outcomes,
    seedOutcomes: setSeededOutcomes,
    queuedIds,
    clearOutcomes: () => {
      setSeededOutcomes(new Map())
      setBurstStatuses({})
    },
    sessionDone,
    dismissSessionDone: () => setSessionDone(false)
  }

  return (
    <DialerRunContext.Provider value={value}>
      {running ? (
        <DialerEngine
          handleRef={engineRef}
          current={current}
          dialNumber={activeDialNumber}
          dialNumberIndex={dialNumberIndex}
          paused={paused}
          stage={stage}
          setStage={setStage}
          queue={queue}
          onMuted={setMuted}
          onDeviceStatus={setDeviceStatus}
          onSummary={(text, personId) => {
            if (
              personId &&
              currentIdRef.current &&
              personId !== currentIdRef.current
            ) {
              return
            }
            setAiSummary(text)
          }}
          onSummaryFailed={() => setSummaryFailed(true)}
          onEndReason={setEndReason}
          onAnsweredBy={setAnsweredBy}
          onCalleeRinging={setCalleeRinging}
          onSessionId={(id) => {
            liveSessionRef.current = id
            sessionWaitRef.current?.resolve(id)
          }}
        />
      ) : null}
      {children}
    </DialerRunContext.Provider>
  )
}

// Owns the Twilio device for the duration of a run. Renders nothing.
function DialerEngine({
  handleRef,
  current,
  dialNumber,
  dialNumberIndex,
  paused,
  stage,
  setStage,
  queue,
  onMuted,
  onDeviceStatus,
  onSummary,
  onSummaryFailed,
  onEndReason,
  onAnsweredBy,
  onSessionId,
  onCalleeRinging
}: {
  handleRef: React.MutableRefObject<EngineHandle | null>
  current: RunPerson | null
  paused: boolean
  stage: Stage
  setStage: (s: Stage) => void
  dialNumber: DialNumber | null
  dialNumberIndex: number
  queue: RunPerson[]
  onMuted: (m: boolean) => void
  onDeviceStatus: (s: string) => void
  onSummary: (s: string | null, personId?: string) => void
  onSummaryFailed: () => void
  onEndReason: (r: EndReason) => void
  onAnsweredBy: (v: string | null) => void
  onSessionId: (id: number | null) => void
  onCalleeRinging: (v: boolean) => void
}) {
  const {
    status: deviceStatus,
    makeCall,
    hangup,
    toggleMute,
    muted,
    logCall,
    ensureSession,
    enqueueQueue,
    reserveCall,
    markQueueEntryStatus,
    watchCallStream,
    sendDigits,
    endSession,
    notReadyRef
  } = useVoiceDevice()

  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const callerConfig = useVoiceSessionStore((s) => s.callerConfig)
  const callerNumber = useVoiceSessionStore((s) => s.callerNumber)
  const callingHours = useVoiceSessionStore((s) => s.callingHours)

  const sessionIdRef = useRef<number | null>(null)
  // person id -> dialer_queue row id, from the enqueue response. reserveCall
  // and markQueueEntryStatus both address the QUEUE ENTRY, not the person, so
  // without this map there is no honest id to hand them.
  const queueIdsRef = useRef<Record<string, number>>({})
  const sessionReadyRef = useRef<Promise<void> | null>(null)
  const reservedCallIdRef = useRef<number | null>(null)
  const callSidRef = useRef<string | null>(null)
  const logTargetsRef = useRef<
    Record<string, { sid: string | null; callId: number | null }>
  >({})
  const rememberLogTarget = (
    personId: string | undefined,
    patch: { sid?: string | null; callId?: number | null }
  ) => {
    if (!personId) return
    const key = String(personId)
    const prev = logTargetsRef.current[key] ?? { sid: null, callId: null }
    logTargetsRef.current[key] = { ...prev, ...patch }
  }
  const stopWatchingRef = useRef<(() => void) | null>(null)
  const watchedSidRef = useRef<string | null>(null)
  const dialTokenRef = useRef(0)
  // Why the last call ended, which decides whether the run rolls on by itself.
  const endReasonRef = useRef<EndReason>(null)
  // Set by the rep pressing Disconnect. finish() runs for every ending, and it
  // reported the answered/no-answer shape of the call, which overwrote the one
  // fact that says the rep was the one who ended it.
  const manualHangupRef = useRef(false)

  useEffect(() => onMuted(muted), [muted, onMuted])

  // THE SOURCE OF TRUTH FOR "IS THIS CALL ANSWERED" IS THE SERVER.
  //
  // call.parameters.CallSid is empty on an outbound Twilio Client call — even
  // inside the "accept" handler — so the SSE status stream could never be
  // opened (it is keyed by sid) and the panel sat on "Ringing" through an
  // 18-second conversation. Twilio's answer webhook lands on dialer-service
  // and updates dialer_calls, so that row is what actually knows.
  //
  // Polled while a call is in flight: it yields the sid (which Hold needs),
  // the ANSWERED transition (which starts the timer and triggers talking
  // points), and the terminal status.
  useEffect(() => {
    // Keeps polling through "wrap": the AI summary is written after the call
    // completes, so stopping the moment it ends would never see it.
    if (
      !current ||
      (stage !== "ringing" && stage !== "live" && stage !== "wrap")
    )
      return
    if (!tokenInfo) return
    let cancelled = false

    const poll = async () => {
      try {
        const res = await getDialerCallHistory(tokenInfo, {
          tab: "all",
          personIds: [current.id]
        })
        if (cancelled) return
        const calls = res?.data?.data?.calls ?? []
        // The row for THIS attempt: newest, and created since we dialled.
        const reservedId = reservedCallIdRef.current
        const attemptSid = callSidRef.current
        const row =
          reservedId != null
            ? calls.find((c) => Number(c.id) === Number(reservedId))
            : attemptSid
              ? calls.find((c) => c.twilio_call_sid === attemptSid)
              : undefined
        if (!row) return
        if (row.twilio_call_sid) {
          callSidRef.current = row.twilio_call_sid
          rememberLogTarget(current?.id, { sid: row.twilio_call_sid })
        }

        // The AI summary comes from its OWN endpoint, which reports
        // pending/ready/failed. Reading it off the list row does not work: the
        // list does not return the column, which is why the panel sat on
        // "Writing the summary…" for a call whose summary was already written.
        if (row.status === "COMPLETED" || row.twilio_call_sid) {
          try {
            const summaryFor = current.id
            const sum = await getDialerCallSummary(
              tokenInfo,
              summaryFor,
              row.twilio_call_sid
            )
            // Generating a summary takes longer than a call takes to end, so this
            // await routinely outlives the person it was about. Publishing without
            // checking is how one rep's call was summarised onto the next one.
            if (cancelled) return
            const text = sum?.data?.summary
            if (text) onSummary(text, String(summaryFor))
            else if (sum?.data?.status === "failed") onSummaryFailed()
          } catch {
            // Still generating, or not reachable yet — the next tick retries.
          }
        }
        // The summary fetch above is a second await, and the run can move
        // during it. Without re-checking, a poll that began on the previous
        // call resumes after a redial has started and reports that call's
        // terminal status against the one now ringing - which is ENG-4375
        // arriving on a slow summary response and not at all on a fast one.
        if (cancelled) return
        if (
          callSidRef.current &&
          row.twilio_call_sid &&
          row.twilio_call_sid !== callSidRef.current
        ) {
          return
        }
        if (row.status === "RINGING") onCalleeRinging(true)
        if (row.answered_by) onAnsweredBy(row.answered_by)
        // A machine verdict overrides whatever the call's status said, and it
        // is allowed to arrive late.
        //
        // An answering machine ANSWERS, so the status stream reports the call
        // answered and this verdict comes separately, after. On a short
        // voicemail the stream has already settled on "answered" and moved to
        // wrap by the time it lands - so checking it only while the call is
        // still live meant the rep was shown Connected for a greeting.
        if (row.answered_by && MACHINE_VERDICTS.includes(row.answered_by)) {
          if (endReasonRef.current !== "machine") {
            endReasonRef.current = "machine"
            onEndReason("machine")
          }
          setStage("wrap")
          return
        }
        if (row.status === "ANSWERED") {
          endReasonRef.current = "answered"
          setStage("live")
        } else if (TERMINAL_CALL_STATUSES.includes(row.status)) {
          if (endReasonRef.current === null) {
            endReasonRef.current =
              row.status === "COMPLETED" && (row.duration_seconds ?? 0) > 0
                ? "answered"
                : "no_answer"
          }
          onEndReason(endReasonRef.current)
          setStage("wrap")
        }
      } catch {
        // A poll that fails is retried by the next tick.
      }
    }

    void poll()
    const t = setInterval(poll, 2000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, stage, tokenInfo])
  useEffect(() => onDeviceStatus(deviceStatus), [deviceStatus, onDeviceStatus])

  // Open the session and queue the run once, when the engine mounts.
  useEffect(() => {
    let cancelled = false
    sessionReadyRef.current = (async () => {
      try {
        const sessionId = await ensureSession(
          completeCallingHours(callingHours),
          true,
          queue.find((p) => p.listId)?.listId ?? null
        )
        if (cancelled) return
        sessionIdRef.current = sessionId ?? null
        onSessionId(sessionId ?? null)
        const dialable = queue.filter((p) => p.phone && /\d/.test(p.phone))
        if (sessionId && dialable.length) {
          const entries = (await enqueueQueue(
            sessionId,
            dialable.map((p) => p.id)
          )) as IDialerQueueEntry[] | null
          if (cancelled) return
          const map: Record<string, number> = {}
          for (const e of entries ?? []) {
            const pid = entryPersonId(e)
            if (pid) map[pid] = e.id
          }
          queueIdsRef.current = map
        }
      } catch {
        if (cancelled) return
        if (sessionIdRef.current == null) onSessionId(null)
        message.error({
          content: "Could not open a calling session",
          key: "dialer-session-failed"
        })
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Place the call for whoever is current. Guarded on stage so a re-render
  // cannot dial the same person twice.
  //
  // WAITS FOR THE DEVICE. The engine mounts at the same moment the run starts,
  // and building plus registering the Twilio device is asynchronous — dialing
  // straight away raced it and makeCall returned null, which surfaced as "the
  // dialer is not connected yet" on the very first call of every session. The
  // effect re-runs when the status changes, so the queue starts the instant the
  // device is ready. The old modal gated on the same `ready` status.
  useEffect(() => {
    if (paused || stage !== "connecting" || !current) return
    if (deviceStatus === "error") {
      message.error("The dialer could not connect. Reload and try again.")
      return
    }
    if (deviceStatus !== "ready") return
    const token = ++dialTokenRef.current
    const stillMine = () => dialTokenRef.current === token
    setStage("ringing")
    ;(async () => {
      // ENG-4375. The status poll finds "the row for this attempt" by the
      // reserved id, falling back to callSidRef. reservedCallIdRef was cleared
      // here and callSidRef was NOT, so a redial re-armed with the PREVIOUS
      // call's sid still set: the poll matched that completed row and pushed
      // stage straight back to "wrap" while the new call was ringing. The
      // device's own accept event then pushed it to "live" again, and the two
      // fought every two seconds - Disconnect flicking red to grey, Redial
      // live during a live call.
      //
      // The old watcher goes with it. It is keyed to the finished call's sid
      // and has nothing left to report.
      endReasonRef.current = null
      manualHangupRef.current = false
      onCalleeRinging(false)
      reservedCallIdRef.current = null
      callSidRef.current = null
      stopWatchingRef.current?.()
      stopWatchingRef.current = null
      watchedSidRef.current = null
      onEndReason(null)
      const to = dialNumber?.phone || current.phone || ""
      if (!to) {
        // No number is a person to skip, not a failure to report. Reported as
        // its own reason rather than folded into no_answer: nothing rang, so
        // the only thing marking that this person went past is the pause
        // before the next one, and that pause is longer.
        endReasonRef.current = "skipped"
        onEndReason("skipped")
        setStage("wrap")
        return
      }

      const params: Record<string, string> = {}
      if (callerConfig.countryMatch && current.location_country) {
        params.calleeCountry = current.location_country
      }
      if (callerConfig.rotation && callerConfig.rotateN) {
        params.rotateAfter = String(callerConfig.rotateN)
      }
      // Also sent directly to voiceAnswer, not just reserveCall — if the
      // reserve step below fails or has no queue entry to attach to,
      // voiceAnswer falls back to inserting the dialer_calls row itself and
      // needs list_id from here to still record it.
      if (current.listId) {
        params.list_id = current.listId
      }

      // Reserve the dialer_calls row before Twilio is invoked, so the log has
      // something to attach to even if the call never connects. Only possible
      // for someone who made it into the queue — reserving against an invented
      // id would be worse than not reserving at all.
      if (sessionReadyRef.current) {
        try {
          await sessionReadyRef.current
        } catch {
          void 0
        }
      }
      if (!stillMine()) return

      const queueId = queueIdsRef.current[current.id]
      if (sessionIdRef.current && queueId) {
        try {
          const reserved = await reserveCall(
            sessionIdRef.current,
            queueId,
            to,
            callerNumber || undefined,
            current.linkedin_url,
            current.listId
          )
          reservedCallIdRef.current = reserved ?? null
          rememberLogTarget(current?.id, { callId: reserved ?? null })
          // Links this leg to the row just reserved. voiceAnswer updates by
          // queue_id; without it every call inserted a SECOND dialer_calls
          // row (a QUEUED one from the reserve, plus the real one), so the
          // reserved row was orphaned and the queue entry never matched the
          // call that actually happened.
          if (reserved) params.queue_id = String(queueId)
        } catch {
          // Reserving is bookkeeping; a failure must not block the call.
        }
      }

      const call = await makeCall(
        to,
        params,
        current.id,
        completeCallingHours(callingHours),
        current.linkedin_url
      )
      if (!stillMine()) return
      if (!call) {
        // The device is ready by this point, so a null call is a real
        // failure: microphone refused, or a token that would not mint.
        const reason = notReadyRef?.current
        message.error(
          reason === "mic"
            ? "Allow microphone access to place calls"
            : reason === "token"
              ? "Could not authenticate the call. Reload and try again."
              : "Could not place that call"
        )
        setStage("wrap")
        return
      }
      const c = call as unknown as {
        on: (e: string, fn: () => void) => void
        disconnect?: () => void
        parameters?: { CallSid?: string }
      }

      // Disconnect pressed while this call was still being placed.
      //
      // makeCall is awaited, so between "ringing" appearing and the Call object
      // existing there is nothing for hangup() to act on: it no-opped, the
      // panel went to Call ended because disconnect sets that itself, and then
      // makeCall resolved and rang the prospect anyway. The rep watched a call
      // they had already cancelled connect.
      if (manualHangupRef.current) {
        try {
          c.disconnect?.()
        } catch {
          // Already gone; hangup below is the backstop.
        }
        hangup()
        return
      }

      // The sid arrives WITH "accept", not before it.
      //
      // On an outbound call call.parameters carries the params we sent; Twilio
      // fills CallSid in when the leg is established. Reading it any earlier
      // returned undefined, so the status stream was never opened — no
      // ANSWERED, no timer, and Hold had no leg to act on.
      const beginWatching = () => {
        if (!stillMine()) return
        const sid = c.parameters?.CallSid ?? null
        callSidRef.current = sid
        rememberLogTarget(current?.id, { sid })
        if (!sid || watchedSidRef.current === sid) return
        stopWatchingRef.current?.()
        watchedSidRef.current = sid
        // The REAL pickup comes from the backend: Twilio's answer webhook
        // lands server-side and the status turns ANSWERED. That is what makes
        // the timer measure talk time rather than ring time, and it is what
        // triggers talking-point generation on the server.
        stopWatchingRef.current = watchCallStream(sid, {
          onStatus: (status) => {
            if (status === "ANSWERED") {
              endReasonRef.current = "answered"
              setStage("live")
            } else if (TERMINAL_CALL_STATUSES.includes(status)) {
              // Nobody picked up. That is the case that rolls on by itself.
              if (endReasonRef.current === null) {
                endReasonRef.current =
                  status === "COMPLETED" ? "answered" : "no_answer"
              }
              onEndReason(endReasonRef.current)
              setStage("wrap")
            }
          },
          onSummary,
          onFailed: () => {}
        })
      }

      c.on("accept", beginWatching)
      // Belt and braces: if accept already fired before these handlers were
      // attached, the sid may be sitting there now.
      beginWatching()

      const finish = () => {
        // These are bound to THIS leg's Call object, which outlives the leg.
        // A redial places a new call while the old one is still tearing down,
        // and its disconnect then ran against the attempt that replaced it:
        // the panel showed Call ended through the whole ring, and only came
        // back when the callee answered and accept forced it live again.
        if (!stillMine()) return
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
        if (manualHangupRef.current) endReasonRef.current = "manual"
        else if (endReasonRef.current === null)
          endReasonRef.current = "no_answer"
        onEndReason(endReasonRef.current)
        setStage("wrap")
      }
      c.on("disconnect", finish)
      c.on("cancel", finish)
      c.on("reject", finish)
      c.on("error", finish)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, stage, current?.id, deviceStatus, dialNumberIndex])

  useEffect(
    () => () => {
      stopWatchingRef.current?.()
      stopWatchingRef.current = null
      watchedSidRef.current = null
    },
    []
  )

  // Publish the device-backed actions upward.
  useEffect(() => {
    handleRef.current = {
      hangup: () => {
        manualHangupRef.current = true
        hangup()
      },
      // Tears down a run's call on BOTH sides, as far as this app can reach:
      //
      //   1. the browser leg — Call.disconnect() on the live Twilio Call, plus
      //      the status stream that was following it.
      //   2. the server-side SESSION — dialer_sessions marked complete, so a
      //      run the rep walked away from does not stay open and the next
      //      ensureSession does not resume it.
      //
      // WHAT THIS CANNOT DO: there is no call-level hangup on dialer-service to
      // call from here. /voice/calls/:sid exposes hold, voicemail and decline
      // only (decline is for an inbound ring, not an outbound leg), and
      // completing the session does not touch a leg that is still up. Twilio
      // normally drops the PSTN leg when the client leg it was dialled for
      // disconnects, and that is the only thing stopping the callee's phone
      // today. If it ever fails to propagate, nothing here can end that leg —
      // dialer-service needs to expose a hangup for the sid.
      endCall: async () => {
        hangup()
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
        await endSession()
      },
      hangupCall: () => {
        hangup()
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
      },
      toggleMute,
      sendDigits,
      // Hold is a server action on the live leg: dialer-service moves the
      // caller to hold music. Without a call sid there is no leg to hold, so
      // it reports that instead of flipping a button that does nothing.
      setHold: async (hold) => {
        if (!tokenInfo || !callSidRef.current) {
          message.info("Nothing to hold yet")
          return false
        }
        try {
          await setCallHold(tokenInfo, callSidRef.current, hold)
          return true
        } catch {
          message.error(hold ? "Could not hold the call" : "Could not resume")
          return false
        }
      },
      // Snooze marks the queue entry SKIPPED so the session record shows the
      // rep passed on them deliberately rather than the call simply not
      // happening.
      markSkipped: async () => {
        const queueId = current ? queueIdsRef.current[current.id] : undefined
        if (!sessionIdRef.current || !queueId) return
        await markQueueEntryStatus(
          sessionIdRef.current,
          queueId,
          "SKIPPED"
        ).catch(() => {})
      },
      log: async ({ disposition, notes, personId }) => {
        try {
          const target = personId
            ? logTargetsRef.current[String(personId)]
            : undefined
          const sid = target ? target.sid : callSidRef.current
          const callId = target ? target.callId : reservedCallIdRef.current
          await logCall(sid, {
            disposition,
            notes,
            fallbackCallId: callId ?? undefined
          })
          const logKey = personId ? String(personId) : null
          const queueId = logKey
            ? queueIdsRef.current[logKey]
            : current
              ? queueIdsRef.current[current.id]
              : undefined
          // Sent without waiting; a failure is ignored.
          if (sessionIdRef.current && queueId) {
            void markQueueEntryStatus(
              sessionIdRef.current,
              queueId,
              "CALLED"
            ).catch(() => {})
          }
          if (logKey) delete logTargetsRef.current[logKey]
          // The live refs follow the call in flight, not the one just written,
          // so they are cleared whenever the log was for the person the run is
          // on. Gating this on whether a per-person target was used left the
          // finished call's sid in place for every normal log, which is the
          // state ENG-4375 is about: the poll then matches that completed row
          // on the next attempt. They are left alone only when the log belongs
          // to somebody the run has already moved past.
          if (!logKey || (current && String(current.id) === logKey)) {
            callSidRef.current = null
            reservedCallIdRef.current = null
          }
          return true
        } catch {
          message.error("Could not save the call log")
          return false
        }
      }
    }
    return () => {
      handleRef.current = null
    }
  }, [
    handleRef,
    hangup,
    toggleMute,
    logCall,
    markQueueEntryStatus,
    endSession,
    current,
    tokenInfo
  ])

  return null
}
